import { useCallback, useState } from "react"

import { useCore } from "@hooks/useCore"
import { useSanity } from "@hooks/useSanity"
import { useImage } from "@hooks/useImage"
import { useRoutes } from "@hooks/useRoutes"
import { format } from "date-fns"

export const useArticles = () => {
  const [loading, setLoading] = useState(false)
  const [articles, setArticles] = useState(false)
  const {
    helpers: { edgeNormaliser },
  } = useCore()
  const { getGatsbyImage } = useImage()
  const { urlResolver } = useRoutes()
  const { sanityFetch } = useSanity()

  const fetchArticles = useCallback(
    async categorySlug => {
      setLoading(true)
      const articles = await sanityFetch(`
        *[_type == "article" && category->handle.current == "${categorySlug}"] | order(_createdAt desc) [0...12] {
          _id,
          title,
          description,
          url,
          "handle": handle.current,
          "category": category->{
            title,
            "handle": handle.current,
          },
          attributes {
            readDuration,
            publishedAt,
          },
          "image": image {
            ...,
            asset->
          },
        }
      `)
      setLoading(false)
      setArticles(articles?.length ? articles : [])
    },
    [sanityFetch]
  )

  const getArticles = useCallback(
    (categorySlug: string | null = null): any[] => {
      if (!articles?.length && !loading) {
        fetchArticles(categorySlug)
      }

      return edgeNormaliser(articles)?.filter(({ category }: any) => !categorySlug || category?.handle === categorySlug)
    },
    [articles, edgeNormaliser, fetchArticles, loading]
  )

  const normaliseArticles = useCallback(
    (articles: GatsbyTypes.SanityArticle[]): NormalisedArticle[] => {
      return articles
        ?.map(article => ({
          key: article?._id,
          title: article?.title,
          link: urlResolver(article),
          image: getGatsbyImage(article?.image),
          description: article?.description,
          attributes: {
            ...article?.attributes,
            author: article?.attributes?.author?.name,
            publishedAt: article?.attributes?.publishedAt && format(new Date(article?.attributes?.publishedAt), "dd MMMM yyyy"),
          },
          category: {
            handle: article?.category?.handle?.current,
            title: article?.category?.title,
          },
        }))
        ?.filter(article => article?.key)
    },
    [getGatsbyImage, urlResolver]
  )

  return {
    getArticles,
    normaliseArticles,
  }
}
